import React from 'react';
import { Link } from 'react-router-dom';

const MarketplacePolicies = () => {
  return (
    <div className="page policy marketplace-policies">
      <h1>Marketplace Policies</h1>
      <p>
        <strong>Last updated:</strong> March 24, 2026
      </p>
      <p>
        These policies apply to CarEx Marketplace (&quot;CarEx,&quot; &quot;we,&quot; or
        &quot;us&quot;), a product of <strong>Inspire The Next Inc.</strong> For account
        and general help, see our <Link to="/support">Support</Link> page and{' '}
        <Link to="/terms">Terms of Service</Link>.
      </p>

      <section>
        <h2>Business identification</h2>
        <p>
          <strong>Business name:</strong> CarEx Marketplace
        </p>
        <p>
          <strong>Legal entity:</strong> Inspire The Next Inc.
        </p>
        <p>
          CarEx Marketplace connects buyers and sellers of motor vehicles and related
          services. We provide the online platform, account tools, and—where
          offered—payment facilitation or other features described in our apps and
          checkout flows. Unless we are the direct seller of an item, the seller—not
          CarEx—is responsible for the vehicle, its condition, title, and delivery.
        </p>
      </section>

      <section>
        <h2>Goods and services</h2>
        <p>Through CarEx Marketplace you may access, among other things:</p>
        <ul>
          <li>Listings for new and used vehicles offered by third-party sellers</li>
          <li>Tools to communicate with sellers, save searches, and manage listings</li>
          <li>Optional paid features or platform services, as disclosed at purchase</li>
        </ul>
        <p>
          Specific fees, features, and availability are shown in the product or app at
          the time you use them and should match what you have provided to your payment
          provider (for example, Stripe).
        </p>
      </section>

      <section>
        <h2>Customer service contact</h2>
        <p>Reach customer service using either method below:</p>
        <ul>
          <li>
            Email:{' '}
            <a href="mailto:support@carexmarket.com">support@carexmarket.com</a>
          </li>
          <li>Phone: +1 (917) 376-3763</li>
        </ul>
        <p>
          We aim to respond to email within two business days. For privacy-related
          requests, you may also contact{' '}
          <a href="mailto:privacy@carexmarket.com">privacy@carexmarket.com</a> as
          described in our <Link to="/privacy-policy">Privacy Policy</Link>.
        </p>
      </section>

      <section>
        <h2>Return policy (vehicles and physical goods)</h2>
        <p>
          Vehicles are physical goods. Return, exchange, and inspection rights depend on
          the agreement between you and the seller, plus applicable law in your
          jurisdiction. CarEx is not automatically a party to the sale contract between
          buyer and seller unless we expressly state otherwise for a given transaction.
        </p>
        <p>
          <strong>Process:</strong> Before completing a purchase, review the listing,
          seller disclosures, and any purchase or vehicle-specific terms. If a problem
          arises after purchase, contact the seller first using the communication tools
          or contact information provided in the transaction. If you cannot resolve the
          issue with the seller, contact us at{' '}
          <a href="mailto:support@carexmarket.com">support@carexmarket.com</a> with your
          order or listing reference, a description of the issue, and any relevant
          documentation. We will review whether we can assist under our policies and any
          buyer/seller programs that may apply.
        </p>
      </section>

      <section>
        <h2>Refunds and disputes</h2>
        <p>
          <strong>Platform fees:</strong> Fees paid directly to CarEx or Inspire The
          Next Inc. for subscriptions, listings, or other platform services are governed
          by the terms shown at checkout. Refund eligibility for those fees is stated
          there or in these policies; contact support with your receipt or transaction ID.
        </p>
        <p>
          <strong>Vehicle or third-party purchases:</strong> When you pay a seller (or
          another party) through a flow we facilitate, refund rights may depend on the
          seller, the payment method, and applicable law. If you believe a charge is
          incorrect or unauthorized, contact us promptly at{' '}
          <a href="mailto:support@carexmarket.com">support@carexmarket.com</a> or by
          phone. You may also have rights to dispute a charge with your bank or card
          issuer under payment-network rules and law.
        </p>
        <p>
          We cooperate with good-faith dispute resolution. Providing accurate
          transaction details and responding to our requests helps us review your case.
        </p>
      </section>

      <section>
        <h2>Cancellation</h2>
        <ul>
          <li>
            <strong>Account:</strong> You may stop using the service at any time. To
            request account closure or data handling, contact support. Some records may be
            retained as required by law or as described in our Privacy Policy.
          </li>
          <li>
            <strong>Listings:</strong> You may cancel or withdraw a listing according to
            the in-app tools and our Terms of Service, subject to any active transaction
            or obligation involving a buyer.
          </li>
          <li>
            <strong>Paid features:</strong> Recurring or prepaid services can be canceled
            as described at purchase (e.g., renewal settings in your account or the
            cancellation terms shown for that product). Where required by law, we honor
            applicable cooling-off or statutory cancellation rights.
          </li>
        </ul>
      </section>

      <section>
        <h2>Legal and export restrictions</h2>
        <p>
          Vehicles may be subject to title, registration, emissions, safety, recall, and
          import/export rules in your country or region. You are responsible for
          compliance with applicable laws. Sales across borders may require additional
          documentation, duties, or be prohibited for certain goods. Listings must not
          violate sanctions, export controls, or other legal restrictions. We may remove
          listings or restrict accounts that appear to violate law or our Terms of
          Service.
        </p>
      </section>

      <section>
        <h2>Promotions</h2>
        <p>
          Any discount, credit, contest, referral benefit, or other promotion is subject
          to the official rules or terms displayed with that promotion (including
          eligibility, expiration, stacking limits, and geographic restrictions). We may
          modify or end a promotion where permitted by those rules and law. General
          promotion terms also appear in our <Link to="/terms">Terms of Service</Link>.
        </p>
      </section>
    </div>
  );
};

export default MarketplacePolicies;
